import { AutoAwesome } from '@mui/icons-material';
import {
  customApiClient,
  type GetApiV5FilesPendingReview200,
} from '@liscio/api';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import { GRADIENT_PRIMARY } from 'components/AI/AIStyles';
import { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import { pluralize } from 'utilities/pluralize';
import * as Utilities from 'utilities/utils';
import { DocumentPreviewModal } from 'components/AI/AIFileReview/components/DocumentPreviewModal/DocumentPreviewModal';
import { format, parseISO } from 'date-fns';

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(1.25),
  background: GRADIENT_PRIMARY,
  borderRadius: '50%',
  color: theme.palette.common.white,
}));

export interface ReviewModalProps {
  onClose: () => void;
  /**
   * Old ugly dependency to refresh the list in parent component
   */
  component: any;
}

export const ReviewModal = ({ onClose, component }: ReviewModalProps) => {
  const [selectedFile, setSelectedFile] = useState<number | null>(null);
  const [files, setFiles] = useState<GetApiV5FilesPendingReview200['files']>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const perPage = 20;
  const isFetchingRef = useRef(false);
  const isInitialLoad = isLoading && files.length === 0;

  const loadPage = async (nextPage: number) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    try {
      const data = await customApiClient<GetApiV5FilesPendingReview200>({
        url: '/api/v5/files/pending_review',
        method: 'GET',
        params: { page: nextPage, per_page: perPage },
      });
      setCount(data.count ?? data.files.length);
      setFiles((prev) => {
        const nextFiles = nextPage === 1 ? data.files : [...prev, ...data.files];
        const total = data.count ?? nextFiles.length;
        setHasMore(nextFiles.length < total);
        return nextFiles;
      });
      setPage(nextPage);
    } catch {
      // Surface a failed (re)load instead of swallowing it — a silent failure
      // here would re-create the original stale-list bug. LISD-2500.
      Utilities.AlertMessage('error', 'Failed to load files for review.', 3000);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    loadPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Modal
        isOpen
        className="customModal customModal--center customModal--alert customModal--w700 customModal--nopadding"
      >
        <ModalHeader className="ModalHeader">
          <button
            type="button"
            onClick={onClose}
            className="close"
            aria-label="Close"
          >
            <i className="icon-close2" />
          </button>
        </ModalHeader>
        <ModalHeader className="modalHeader">
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <IconContainer>
              <AutoAwesome fontSize="small" />
            </IconContainer>
            <div className="text-medium pl-2">
              <div>Files Need Review</div>
            </div>

            {Boolean(count) && (
              <span className="tag tag--fill" key="test">
                {`${count} `}
                {pluralize('file', count)}
              </span>
            )}
          </Stack>
        </ModalHeader>
        <ModalBody className="modalContent">
          <Card elevation={0} sx={{ borderRadius: 0 }}>
            <CardHeader
              title="Incoming Files"
              subheader="Files requiring review and processing"
              titleTypographyProps={{ variant: 'h6' }}
              subheaderTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
              }}
              sx={{ pb: 0 }}
            />
            <CardContent>
              {isInitialLoad && <Skeleton width={300} height={50} />}
              {!isInitialLoad && files.length === 0 && (
                <Typography>No files to review</Typography>
              )}
              {Boolean(files.length) && (
                <List
                  sx={{ maxHeight: '50vh', overflowY: 'auto' }}
                  onScroll={(event) => {
                    const target = event.currentTarget;
                    if (
                      hasMore &&
                      !isLoading &&
                      target.scrollTop + target.clientHeight >= target.scrollHeight - 50
                    ) {
                      loadPage(page + 1).catch(() => {});
                    }
                  }}
                >
                  {files.map((file) => {
                    const logo = Utilities.fileType({
                      content_type: file.content_type,
                    });
                    return (
                      <ListItemButton
                        key={file.id}
                        onClick={() => {
                          if (file.id) {
                            setSelectedFile(file.id);
                          }
                        }}
                      >
                        <ListItemIcon>
                          <span
                            className="fileTtype align-self-center"
                            style={{ color: '#212529' }}
                          >
                            <i className={logo} aria-hidden="true" />
                          </span>
                        </ListItemIcon>
                        <ListItemText
                          primary={file.display_name}
                          secondary={`${file.uploaded_by} • ${format(
                            parseISO(file.created_at),
                            'MM/dd/yyyy',
                          )}`}
                        />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </ModalBody>
      </Modal>
      <DocumentPreviewModal
        showModal={!!selectedFile}
        closeModal={() => {
          setSelectedFile(null);
          // Returning from the file view (e.g. after marking it reviewed in
          // DocumentShow) must refresh this list + the header count badge.
          // Both live in local state here, so they don't react to the
          // pending_review query-cache invalidation that
          // MarkAsReviewedConfirmationModal does for the toolbar button. Reload
          // page 1 so the just-reviewed file drops out. LISD-2500.
          loadPage(1);
        }}
        id={selectedFile}
        component={component}
      />
    </>
  );
};
